import { PBKDF2_ITERATIONS, decryptSecret, encryptSecret } from "./crypto";
import {
  type UnlockDatabase,
  VAULT_ID,
  deleteVaultRecord,
  openUnlockDb,
  readVaultRecord,
} from "./db";
import type { ReloginSecret, UnlockResult, VaultRecord, VaultStatus } from "./types";

export const MAX_UNLOCK_ATTEMPTS = 10;

let connection: Promise<UnlockDatabase> | null = null;

function database(): Promise<UnlockDatabase> {
  if (!connection) {
    connection = openUnlockDb();
  }
  return connection;
}

export async function closeUnlockStore(): Promise<void> {
  if (!connection) {
    return;
  }
  const db = await connection;
  connection = null;
  db.close();
}

const EMPTY_STATUS: VaultStatus = {
  saved: false,
  label: null,
  attemptsLeft: MAX_UNLOCK_ATTEMPTS,
};

function ownRecord(record: VaultRecord | null, ownerKey: string | null): VaultRecord | null {
  if (!record || ownerKey === null || record.ownerKey !== ownerKey) {
    return null;
  }
  return record;
}

function statusOf(record: VaultRecord | null): VaultStatus {
  if (!record) {
    return EMPTY_STATUS;
  }
  return {
    saved: true,
    label: record.label,
    attemptsLeft: Math.max(0, MAX_UNLOCK_ATTEMPTS - record.failedAttempts),
  };
}

export async function vaultStatus(ownerKey: string | null): Promise<VaultStatus> {
  const db = await database();
  return statusOf(ownRecord(await readVaultRecord(db), ownerKey));
}

export async function hasSavedSecret(ownerKey: string | null): Promise<boolean> {
  return (await vaultStatus(ownerKey)).saved;
}

export async function saveSecret(
  ownerKey: string,
  pin: string,
  secret: ReloginSecret,
  label: string,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<void> {
  const payload = await encryptSecret(pin, secret, iterations);
  const db = await database();
  await db.put("vault", {
    id: VAULT_ID,
    ownerKey,
    label,
    failedAttempts: 0,
    savedAt: new Date().toISOString(),
    ...payload,
  });
}

export async function forgetSecret(): Promise<void> {
  const db = await database();
  await deleteVaultRecord(db);
}

export async function unlockSecret(ownerKey: string | null, pin: string): Promise<UnlockResult> {
  const db = await database();
  const record = ownRecord(await spendAttempt(db, ownerKey), ownerKey);
  if (!record) {
    return { ok: false, reason: "missing", attemptsLeft: MAX_UNLOCK_ATTEMPTS };
  }

  const secret = await decryptSecret(pin, record);
  if (secret) {
    await db.put("vault", { ...record, failedAttempts: 0 });
    return { ok: true, secret, attemptsLeft: MAX_UNLOCK_ATTEMPTS };
  }

  if (record.failedAttempts >= MAX_UNLOCK_ATTEMPTS) {
    await deleteVaultRecord(db);
    return { ok: false, reason: "wiped", attemptsLeft: 0 };
  }
  return {
    ok: false,
    reason: "wrong-pin",
    attemptsLeft: MAX_UNLOCK_ATTEMPTS - record.failedAttempts,
  };
}

async function spendAttempt(
  db: UnlockDatabase,
  ownerKey: string | null,
): Promise<VaultRecord | null> {
  const transaction = db.transaction("vault", "readwrite");
  const stored = (await transaction.store.get(VAULT_ID)) ?? null;
  const record = ownRecord(stored, ownerKey);
  if (!record) {
    await transaction.done;
    return null;
  }
  const spent: VaultRecord = { ...record, failedAttempts: record.failedAttempts + 1 };
  await transaction.store.put(spent);
  await transaction.done;
  return spent;
}
